// SPDX-License-Identifier: BUSL-1.1

/**
 * Teams analytics dashboard.
 *
 * Shows aggregated telemetry per team based on ops_agent_sessions metadata.
 * Aggregations include: total cost, active time, session counts, and outcomes.
 *
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, DollarSign, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { apiFetch } from '@/lib/api-client';
import { useOrg } from '@/contexts/org-context';
import { DataTable } from '@/components/data-table';
import { TableSkeleton } from '@/components/skeletons';
import { showError } from '@/lib/toast';

interface TeamStats {
  teamSlug: string;
  totalSessions: number;
  totalCostUsd: number;
  totalActiveSeconds: number;
  successCount: number;
  failedCount: number;
  manualTerminateCount: number;
  budgetExceededCount: number;
}

interface TeamsResponse {
  data: TeamStats[];
  teams: Array<{ slug: string; name: string }>;
}

function formatCost(usd: number): string {
  return `$${usd.toFixed(2)}`;
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

function StatCard({
  label,
  value,
  icon: Icon,
  className = '',
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  className?: string;
}) {
  return (
    <div className={`flex items-center gap-3 rounded-lg border border-neutral-800 bg-neutral-900/50 p-4 ${className}`}>
      <Icon className="size-5 text-emerald-400" />
      <div className="flex flex-col">
        <span className="text-2xl font-bold">{value}</span>
        <span className="text-xs text-neutral-500">{label}</span>
      </div>
    </div>
  );
}

// Columns definition for DataTable
const teamColumns: ColumnDef<TeamStats>[] = [
  {
    accessorKey: 'teamSlug',
    header: 'Team',
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <Users className="size-4 text-neutral-500" />
        <span className="font-medium">@{row.getValue('teamSlug')}</span>
      </div>
    ),
  },
  {
    accessorKey: 'totalCostUsd',
    header: 'Cost',
    cell: ({ row }) => (
      <span className="font-mono">{formatCost(row.getValue('totalCostUsd'))}</span>
    ),
  },
  {
    accessorKey: 'totalActiveSeconds',
    header: 'Active Time',
    cell: ({ row }) => formatDuration(row.getValue('totalActiveSeconds')),
  },
  {
    accessorKey: 'totalSessions',
    header: 'Sessions',
  },
  {
    accessorKey: 'successCount',
    header: 'Success',
    cell: ({ row }) => (
      <div className="flex items-center gap-1">
        <CheckCircle2 className="size-3 text-green-400" />
        <span className="text-green-400">{row.getValue('successCount')}</span>
      </div>
    ),
  },
  {
    accessorKey: 'failedCount',
    header: 'Failed',
    cell: ({ row }) => (
      <div className="flex items-center gap-1">
        <XCircle className="size-3 text-red-400" />
        <span className="text-red-400">{row.getValue('failedCount')}</span>
      </div>
    ),
  },
  {
    id: 'successRate',
    header: 'Rate',
    cell: ({ row }) => {
      const total = row.original.totalSessions;
      const success = row.original.successCount;
      const rate = total > 0 ? ((success / total) * 100).toFixed(1) : '0.0';
      return (
        <span className="rounded bg-neutral-800 px-2 py-0.5 font-mono text-xs">
          {rate}%
        </span>
      );
    },
  },
];

export function TeamsPage() {
  const { currentOrg } = useOrg();

  const { data, isLoading, error } = useQuery({
    queryKey: ['teams', 'stats', currentOrg?.id],
    queryFn: () => apiFetch<TeamsResponse>('/teams/stats'),
    staleTime: 60_000,
    retry: 1,
  });

  const errorMessage = error instanceof Error ? error.message : null;

  useEffect(() => {
    if (!errorMessage) return;
    showError('Failed to load team stats', errorMessage);
  }, [errorMessage]);

  // Calculate totals
  const totals = data?.data?.reduce(
    (acc, team) => ({
      sessions: acc.sessions + team.totalSessions,
      cost: acc.cost + team.totalCostUsd,
      activeTime: acc.activeTime + team.totalActiveSeconds,
      success: acc.success + team.successCount,
      failed: acc.failed + team.failedCount,
    }),
    { sessions: 0, cost: 0, activeTime: 0, success: 0, failed: 0 },
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Teams</h1>
          <p className="text-sm text-neutral-500">Loading team telemetry...</p>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-3 rounded-lg border border-neutral-800 bg-neutral-900/50 p-4">
              <div className="h-5 w-5 rounded bg-neutral-700 animate-pulse" />
              <div className="space-y-1">
                <div className="h-7 w-16 rounded bg-neutral-700 animate-pulse" />
                <div className="h-3 w-20 rounded bg-neutral-800 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
        <TableSkeleton rows={5} cols={7} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-900/50 bg-red-900/20 p-4">
        <p className="text-red-400">Failed to load team stats</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Teams</h1>
        <p className="text-sm text-neutral-500">
          Agent session telemetry aggregated by team.{' '}
          {currentOrg && <span className="text-emerald-400">@{currentOrg.slug}</span>}
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard
          label="Total Sessions"
          value={totals?.sessions ?? 0}
          icon={Users}
        />
        <StatCard
          label="Total Cost"
          value={formatCost(totals?.cost ?? 0)}
          icon={DollarSign}
        />
        <StatCard
          label="Active Time"
          value={formatDuration(totals?.activeTime ?? 0)}
          icon={Clock}
        />
        <StatCard
          label="Success Rate"
          value={
            totals && totals.sessions > 0
              ? `${((totals.success / totals.sessions) * 100).toFixed(1)}%`
              : 'N/A'
          }
          icon={CheckCircle2}
        />
      </div>

      {/* Teams table using DataTable */}
      <DataTable
        columns={teamColumns}
        data={data?.data ?? []}
        emptyMessage="No team data yet. Sessions will appear here once agents report their team_slug."
      />

      {/* Available teams config */}
      {data?.teams && data.teams.length > 0 && (
        <div className="rounded-lg border border-neutral-800 bg-neutral-900/30 p-4">
          <h3 className="mb-2 text-sm font-semibold">Configured Teams</h3>
          <div className="flex flex-wrap gap-2">
            {data.teams.map((team) => (
              <span
                key={team.slug}
                className="rounded bg-neutral-800 px-2 py-1 text-xs text-neutral-300"
              >
                @{team.slug}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
